import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'

import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  dv360BulkTargetingWasUpdatedSelector,
  searchDv360TargetingOptionsIsLoadingSelector,
  searchDv360TargetingOptionsSelector,
  dv360TargetingSelector,
  updateDv360BulkTargetingErrorSelector,
  updateDv360BulkTargetingIsLoadingSelector
} from '../../../../../../modules/selectors/dv360Constants'
import {
  clearUpdateDv360BulkTargeting,
  searchDv360TargetingOptions,
  updateDv360BulkTargeting
} from '../../../../../../modules/actions/dv360Constants'

import { HISTORY_META_EDIT_SECTION, TYPE_LINE_ITEM } from '../../../../../../constants/changeHistory'
import { targetingTypes } from '../../../LineItemDv360Create/fields'
import { COUNTRY_OR_REGION, getInitialValues } from './fields'

function GeolocationSectionForm({ editItemData: item, title, ...formProps }) {
  const dispatch = useDispatch()

  const targeting = useSelector(dv360TargetingSelector)
  const dv360BulkTargetingWasUpdated = useSelector(dv360BulkTargetingWasUpdatedSelector)
  const searchTargetingOptionsResults = useSelector(searchDv360TargetingOptionsSelector)
  const searchTargetingOptionsIsLoading = useSelector(searchDv360TargetingOptionsIsLoadingSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const initialValues = useMemo(() => {
    return getInitialValues(targeting)
  }, [targeting])

  const handleSubmit = useCallback(
    values => {
      const requests = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_GEO_REGION,
          assigned_targeting_options: values[COUNTRY_OR_REGION].map(item => ({
            targeting_option_id: item.value
          }))
        }
      ]

      const updateData = {
        account: selectedAdAccountId,
        line_item_id: item.id,
        requests,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: item.id,
            item_type: TYPE_LINE_ITEM,
            action: 'update'
          }
        }
      }

      dispatch(updateDv360BulkTargeting(updateData, true))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = purifiedFormik

  const formattedGeolocations = useMemo(
    () =>
      searchTargetingOptionsResults.map(item => ({
        label: item.geo_region_details.name,
        value: item.id
      })),
    [searchTargetingOptionsResults]
  )

  const getGeolocations = useCallback(
    searchValue => {
      if (searchValue) {
        dispatch(
          searchDv360TargetingOptions({
            account: selectedAdAccountId,
            targeting_type: targetingTypes.TARGETING_TYPE_GEO_REGION,
            search_terms: searchValue
          })
        )
      }
    },
    [dispatch, selectedAdAccountId]
  )

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateDv360BulkTargeting())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || dv360BulkTargetingWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={updateDv360BulkTargetingErrorSelector}
      isLoadingSelector={updateDv360BulkTargetingIsLoadingSelector}
      {...formProps}
    >
      <MultiSelectBox
        name={COUNTRY_OR_REGION}
        options={formattedGeolocations}
        value={values[COUNTRY_OR_REGION]}
        isLoading={searchTargetingOptionsIsLoading}
        setFieldValue={setFieldValue}
        onSearch={getGeolocations}
      />
    </EditForm>
  )
}

export default GeolocationSectionForm
